import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Linkedin, Youtube, Mail, MapPin } from 'lucide-react';
import { solutionMenuItems } from '@/data/solutions/registry';
const LOGO_URL = '/taxera-logo-white.png';
const COLUMNS = [
  {
    title: 'Solutions',
    links: [
      { href: '/solutions/e-invoicing', label: 'e-Invoicing' },
      ...solutionMenuItems().map((m) => ({ href: m.href, label: m.label })),
      { href: '/solutions/saf-t', label: 'SAF-T' },
      { href: '/solutions/vat-returns', label: 'VAT returns' },
      { href: '/solutions/ecompliance', label: 'eCompliance' },
      { href: '/solutions/tax-finance-operations', label: 'Tax & Finance Operations' },
      { href: '/sap-s4hana', label: 'SAP S/4HANA' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { href: '/tracker', label: 'Global Tax Tracker' },
      { href: '/blog', label: 'Blog' },
      { href: '/news', label: 'News' },
      { href: '/mandates', label: 'Mandates' },
      { href: '/knowledge-base', label: 'Country guides' },
      { href: '/resources/videos', label: 'Videos' },
    ],
  },
  {
    title: 'Company',
    links: [
      { href: '/about', label: 'About' },
      { href: '/partners', label: 'Partners' },
      { href: '/media', label: 'Media' },
      { href: '/security', label: 'Security' },
      { href: '/contact', label: 'Contact' },
    ],
  },
];
const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const legalLinks = [
    { href: '/privacy', label: t('footer.privacy') },
    { href: '/terms', label: t('footer.terms') },
    { href: '/cookies', label: t('footer.cookies') },
    { href: '/imprint', label: t('footer.imprint') },
  ];
  return (
    <footer className="bg-taxera-charcoal text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          {/* Brand + contact */}
          <div className="lg:col-span-2 max-w-sm">
            <Link to="/" className="inline-block mb-4" data-testid="footer-logo">
              <img src={LOGO_URL} alt="Taxera Technologies" className="h-10" />
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">{t('footer.description')}</p>
            <div className="space-y-2.5 text-sm text-slate-400">
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-taxera-teal shrink-0" />
                <span>Sihleggstrasse 23, 8832 Wollerau, Switzerland</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-taxera-teal shrink-0" />
                <a href="mailto:office@taxera.tech" className="hover:text-white transition-colors">office@taxera.tech</a>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <a href="https://www.linkedin.com/company/taxera/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-taxera-teal transition-colors" aria-label="LinkedIn" data-testid="footer-linkedin">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="https://www.youtube.com/@taxera" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-taxera-teal transition-colors" aria-label="YouTube" data-testid="footer-youtube">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>
          {/* Link columns */}
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="text-sm font-semibold text-white mb-3">{col.title}</h4>
              <div className="space-y-2">
                {col.links.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-sm text-slate-400 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
        {/* Divider + bottom bar */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {legalLinks.map((link) => (
              <Link key={link.href} to={link.href} className="text-xs text-slate-500 hover:text-white transition-colors" data-testid={`footer-${link.href.replace('/', '')}`}>
                {link.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>&copy; {currentYear} Taxera Technologies GmbH</span>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span className="hidden sm:inline">CHE-248.949.004</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer;
